import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, CircleAlert, Pencil, Plus, Trash2 } from 'lucide-react';
import { useSurvey } from '../../../assets/js/SurveyContext';
import { useLanguage } from '../../../assets/js/LanguageContext';
import QuestionEditorModal from '../TemplatesPulse/Modal';
import QuestionTypeSelectorModal from '../TemplatesPulse/QuestionTypeSelectorModal';
import TemplateSteps from '../TemplatesPulse/TemplateSteps';
import LaunchSurvey from './LaunchSurvey';
import SectionEyebrow from '../../common/SectionEyebrow';
import Modal from '../../common/Modal';
import '../../../assets/styles/ChooseSurvey.css';
import '../../../assets/styles/TemplateBuilder.css';

const toEditor = (question) => ({
  id: question.id,
  pregunta: question.data?.question || '',
  tipo: question.data?.type || 'open',
  opciones: question.data?.options,
  minValue: question.data?.minValue ?? 0,
  maxValue: question.data?.maxValue ?? 10,
  configurada: Boolean(question.configured),
});

const fromEditor = (edited) => ({
  id: edited.id,
  configured: true,
  data: {
    question: edited.pregunta,
    type: edited.tipo,
    options: edited.tipo === 'closed' ? edited.opciones : undefined,
    minValue: edited.tipo === 'rating' ? edited.minValue : undefined,
    maxValue: edited.tipo === 'rating' ? edited.maxValue : undefined,
  },
});

const DiagnosticQuestions = ({ onEditingQuestion, onBack }) => {
  const { t } = useLanguage();
  const { diagnosticName, questions, setQuestions } = useSurvey();
  const questionTypeLabels = {
    open: t('question_type_open'),
    closed: t('question_type_closed'),
    rating: t('dash_question_type_rating'),
  };
  const [editing, setEditing] = useState(null);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [isLaunched, setIsLaunched] = useState(false);

  const setEditingFlag = (value) => {
    if (onEditingQuestion) onEditingQuestion(value);
  };

  if (isLaunched) {
    return <LaunchSurvey onClose={() => setIsLaunched(false)} />;
  }

  const handleEditQuestion = (question) => {
    setEditing(toEditor(question));
    setEditingFlag(true);
  };

  const handleAddQuestionClick = () => {
    setShowTypePicker(true);
    setEditingFlag(true);
  };

  const handleSelectQuestionType = (type) => {
    setShowTypePicker(false);
    setEditing({
      id: Date.now(),
      pregunta: '',
      tipo: type,
      opciones: type === 'closed' ? ['', ''] : undefined,
      minValue: 0,
      maxValue: 10,
      configurada: false,
    });
  };

  const closeEditor = () => {
    setEditing(null);
    setEditingFlag(false);
  };

  const handleSaveQuestion = (edited) => {
    const saved = fromEditor(edited);
    const exists = questions.some((question) => question.id === saved.id);
    setQuestions(exists
      ? questions.map((question) => (question.id === saved.id ? saved : question))
      : [...questions, saved]);
    closeEditor();
  };

  const confirmDelete = () => {
    setQuestions(questions.filter((question) => question.id !== questionToDelete.id));
    setQuestionToDelete(null);
  };

  // El contexto arranca con tres huecos sin datos: solo se listan las preguntas reales.
  const visibleQuestions = questions.filter((question) => question.data);
  const configuredCount = visibleQuestions.filter((question) => question.configured).length;
  const pendingCount = visibleQuestions.length - configuredCount;

  return (
    <div className="tp-choose tp-builder">
      {onBack && (
        <button type="button" className="tp-choose__back" onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
          {t('common_back')}
        </button>
      )}

      <header className="tp-choose__head">
        <div className="tp-choose__heading">
          <SectionEyebrow>{t('choose_diagnostic')}</SectionEyebrow>
          <h1 className="tp-choose__title">{diagnosticName || t('template_add_name')}</h1>
          <p className="tp-choose__lead">{t('create_builder_lead')}</p>
        </div>
      </header>

      <TemplateSteps current={2} />

      <section className="tp-builder__panel">
        <div className="tp-builder__panel-head">
          <h2 className="tp-builder__count">
            {t('builder_questions_count').replace('{count}', visibleQuestions.length)}
          </h2>
          {pendingCount > 0 && (
            <span className="tp-builder__pending">
              <CircleAlert size={14} strokeWidth={2} aria-hidden="true" />
              {t('builder_pending').replace('{count}', pendingCount)}
            </span>
          )}
        </div>

        {visibleQuestions.length === 0 ? (
          <p className="tp-builder__empty">{t('create_builder_empty')}</p>
        ) : (
          <ol className="tp-builder__list">
            {visibleQuestions.map((question, index) => {
              const text = (question.data?.question || '').trim();
              const pending = !question.configured || !text;
              return (
                <li key={question.id || index} className="tp-builder__item">
                  <span className="tp-builder__num" aria-hidden="true">{index + 1}</span>
                  <div className="tp-builder__body">
                    <p className={`tp-builder__text ${text ? '' : 'is-empty'}`.trim()}>
                      {text || t('builder_no_text')}
                    </p>
                    <p className="tp-builder__meta">
                      {question.data?.type && (
                        <span className={`tp-dmod__type is-${question.data.type}`}>
                          {questionTypeLabels[question.data.type] || t('question_type_none')}
                        </span>
                      )}
                      {pending && <span className="tp-builder__tag">{t('template_question_pending')}</span>}
                    </p>
                  </div>
                  <div className="tp-builder__actions">
                    <button type="button" className="btn btn--outline btn--sm" onClick={() => handleEditQuestion(question)}>
                      <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                      {t('homeqg_edit_modify')}
                    </button>
                    <button type="button" className="btn btn--sm tp-builder__delete" onClick={() => setQuestionToDelete(question)}>
                      <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
                      {t('homeqg_delete')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="tp-builder__panel-actions">
          <button type="button" className="btn btn--secondary tp-builder__add" onClick={handleAddQuestionClick}>
            <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
            {t('create_add_question')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setIsLaunched(true)}
            disabled={configuredCount === 0}
          >
            {t('builder_continue')}
            <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </section>

      <QuestionTypeSelectorModal
        open={showTypePicker}
        onSelect={handleSelectQuestionType}
        onClose={() => { setShowTypePicker(false); setEditingFlag(false); }}
      />

      <QuestionEditorModal
        question={editing}
        onClose={closeEditor}
        onSave={handleSaveQuestion}
      />

      <Modal
        open={Boolean(questionToDelete)}
        onOpenChange={(open) => { if (!open) setQuestionToDelete(null); }}
        variant="delete"
        title={t('builder_delete_title')}
        description={(questionToDelete?.data?.question || '').trim() || t('builder_no_text')}
        cancelText={t('common_cancel')}
        confirmText={t('homeqg_delete')}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default DiagnosticQuestions;
